import moment from "moment";
import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";
import type {
  ShippingPoGenerationResult,
  SuggestedShippingPo,
} from "./shippingPoGeneration.ts";

export interface EditableShippingPoLine {
  sku: string;
  description: string;
  /** Authoritative pieces per sellable pack; absent/invalid means unknown. */
  pieceCount?: number | null;
  /**
   * Immutable per-unit cube derived from the source row at generation time
   * (BUG-7: the PO's total cube must scale with qty, so only the per-unit
   * figure is fixed — the total is always derived via `computePoTotals`).
   */
  cubicFeetPerUnit: number;
  qty: number;
  qtyError: string;
  /** USD per ordered SKU unit; null is explicitly unavailable. Manual edits stay local. */
  unitPriceUsd: number | null;
  priceError: string;
  neededBy: Date | undefined;
}

export interface EditableShippingPo {
  userCreated?: boolean;
  id: string;
  sequence: number;
  totalCount: number;
  division?: string;
  shipDate: string;
  cubeCapacityFt3: number;
  lines: EditableShippingPoLine[];
  /** UI-only Deliver To warehouse key (`${division}_${warehouseID}`); not persisted. */
  deliverTo: string;
  /** UI-only editable ship date; defaults to the generated ship date. */
  shipDateValue: Date | undefined;
  /** UI-only editable arrival date, initialized from live generation when available. */
  arrivalDate: Date | undefined;
}

/** Pure local-only factory. The caller owns collision-free ID allocation. */
export function buildBlankShippingPo(
  id: string,
  sequence: number,
  cubeCapacityFt3: number,
  division?: string
): EditableShippingPo {
  return {
    id,
    sequence,
    totalCount: sequence,
    ...(division ? { division } : {}),
    shipDate: "",
    cubeCapacityFt3,
    lines: [],
    deliverTo: "",
    shipDateValue: undefined,
    arrivalDate: undefined,
    userCreated: true,
  };
}

/** Unavailable is not zero, and deliberate capacity overrides may exceed 100%. */
export function formatLineCapacityPercent(
  po: EditableShippingPo,
  line: EditableShippingPoLine
): string {
  if (
    !Number.isFinite(po.cubeCapacityFt3) ||
    po.cubeCapacityFt3 <= 0 ||
    !Number.isFinite(line.cubicFeetPerUnit) ||
    line.cubicFeetPerUnit < 0 ||
    !Number.isFinite(line.qty) ||
    line.qty < 0
  )
    return "Unavailable";
  const percent =
    ((line.qty * line.cubicFeetPerUnit) / po.cubeCapacityFt3) * 100;
  return Number.isFinite(percent) ? `${percent.toFixed(2)}%` : "Unavailable";
}

export function parseShipDateToDate(shipDate: string): Date | undefined {
  const calendarDate = /^\d{4}-\d{2}-\d{2}/.test(shipDate)
    ? shipDate.slice(0, 10)
    : shipDate;
  const parsed = moment(
    calendarDate,
    ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"],
    true
  );
  return parsed.isValid() ? parsed.toDate() : undefined;
}

/**
 * `BulkOrderCreationRow.cubicFeet` is the row's total cube for its full
 * `requestedUnits` quantity (see that field's doc comment), so the per-unit
 * figure is simply the row total divided by its row quantity.
 */
function cubicFeetPerUnitFor(row: BulkOrderCreationRow): number {
  return row.requestedUnits > 0
    ? Number.isFinite(row.cubicFeet)
      ? row.cubicFeet / row.requestedUnits
      : Number.NaN
    : (row.unitCube ?? Number.NaN);
}

function buildEditableLine(row: BulkOrderCreationRow): EditableShippingPoLine {
  return {
    sku: row.sku,
    description: row.description,
    ...(row.pieceCount === undefined ? {} : { pieceCount: row.pieceCount }),
    cubicFeetPerUnit: cubicFeetPerUnitFor(row),
    qty: row.requestedUnits,
    qtyError: "",
    unitPriceUsd: row.unitPriceUsd ?? null,
    priceError: "",
    neededBy: parseShipDateToDate(row.shipDate),
  };
}

/**
 * Appends a source-order SKU to `po` with qty 0 so the per-SKU aggregate
 * allocation limit is not exceeded until the user enters a quantity. The
 * per-unit cube is still resolved from the source row (BUG-7), so the PO's
 * total cube scales correctly once the user enters a quantity. Returns
 * false when the SKU is already on the PO.
 */
export function addSkuLineToPo(
  po: EditableShippingPo,
  row: BulkOrderCreationRow
): boolean {
  if (po.lines.some((line) => line.sku === row.sku)) return false;
  po.lines.push({
    ...buildEditableLine(row),
    qty: 0,
  });
  return true;
}

function buildEditablePo(
  suggestedPo: SuggestedShippingPo,
  cubeCapacityFt3: number
): EditableShippingPo {
  const division = suggestedPo.rows[0]?.division;
  return {
    id: `shipping-po-${String(suggestedPo.sequence)}`,
    sequence: suggestedPo.sequence,
    totalCount: suggestedPo.totalCount,
    ...(division ? { division } : {}),
    shipDate: suggestedPo.shipDate,
    cubeCapacityFt3,
    lines: suggestedPo.rows.map((row) => buildEditableLine(row)),
    deliverTo: "",
    shipDateValue: parseShipDateToDate(suggestedPo.shipDate),
    arrivalDate: suggestedPo.arrivalDate
      ? parseShipDateToDate(suggestedPo.arrivalDate)
      : undefined,
  };
}

export function buildEditableShippingPos(
  result: ShippingPoGenerationResult,
  cubeCapacityFt3: number
): EditableShippingPo[] {
  return result.suggestedPos.map((po) => buildEditablePo(po, cubeCapacityFt3));
}

export interface ShippingPoTotals {
  totalCubicFeet: number;
  loadPercent: number;
  remainingCapacityFt3: number;
  totalCostUsd: number | null;
}

export type LeftoverMoveMode = "fit" | "override";

export interface LeftoverInventoryMoveDraft {
  selectedSkus: string[];
  sourceLeftoversBySku: Record<string, number>;
  targetPoId: string;
  quantitiesBySku: Record<string, string>;
  mode: LeftoverMoveMode;
  stage: "edit" | "review";
  fitInitialized?: boolean;
  targetDatesByPo: Record<
    string,
    {
      shipDateValue?: Date | undefined;
      arrivalDate?: Date | undefined;
    }
  >;
}

export interface LeftoverInventoryMoveSnapshot {
  rows: BulkOrderCreationRow[];
  targets: EditableShippingPo[];
}

export interface LeftoverInventoryMoveApplyRequest {
  selectedSkus: string[];
  targetPoId: string;
  quantitiesBySku: Record<string, string>;
  mode: LeftoverMoveMode;
  overrideConfirmed: boolean;
  shipDateValue?: Date | undefined;
  arrivalDate?: Date | undefined;
}

export interface LeftoverInventoryMoveApplyResult {
  targetError?: string;
  requiresReview?: boolean;
  quantitiesBySku?: Record<string, number>;
  applied: { sku: string; quantity: number }[];
  skipped: { sku: string; reason: string }[];
}

export function getUtilizationStatus(
  loadPercent: number
): "over-capacity" | "near-capacity" | "under-capacity" {
  if (loadPercent > 100) return "over-capacity";
  if (loadPercent >= 93) return "near-capacity";
  return "under-capacity";
}

export function computePoTotals(po: EditableShippingPo): ShippingPoTotals {
  // BUG-7: cube must reflect current contents, so it is always derived from
  // each line's fixed per-unit cube times its current quantity, never a
  // frozen generation-time total.
  const totalCubicFeet = po.lines.reduce(
    (sum, line) => sum + line.cubicFeetPerUnit * line.qty,
    0
  );
  const totalCostUsd = po.lines.some(
    (line) => line.qty > 0 && line.unitPriceUsd === null
  )
    ? null
    : po.lines.reduce(
        (sum, line) => sum + line.qty * (line.unitPriceUsd ?? 0),
        0
      );
  const loadPercent =
    po.cubeCapacityFt3 > 0
      ? Math.round((totalCubicFeet / po.cubeCapacityFt3) * 100)
      : 0;
  return {
    totalCubicFeet: Math.round(totalCubicFeet * 10) / 10,
    loadPercent,
    remainingCapacityFt3:
      Math.round(Math.max(0, po.cubeCapacityFt3 - totalCubicFeet) * 10) / 10,
    totalCostUsd,
  };
}

/** Display precision only; capacity and allocation calculations keep raw values. */
export function formatMoveCubeFt3(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "Unavailable";
  const fixed = value.toFixed(2);
  const normalized = fixed === "-0.00" ? "0.00" : fixed;
  return `${normalized} ft³${value < 0 ? " (Over capacity)" : ""}`;
}

export function getRawPoCube(po: EditableShippingPo): number | undefined {
  if (!Number.isFinite(po.cubeCapacityFt3) || po.cubeCapacityFt3 <= 0) {
    return undefined;
  }

  let totalCube = 0;
  for (const line of po.lines) {
    if (
      !Number.isFinite(line.qty) ||
      line.qty < 0 ||
      !Number.isFinite(line.cubicFeetPerUnit) ||
      line.cubicFeetPerUnit < 0
    ) {
      return undefined;
    }
    totalCube += line.qty * line.cubicFeetPerUnit;
    if (!Number.isFinite(totalCube)) return undefined;
  }
  return totalCube;
}

export function getAvailableCapacityFt3(
  po: EditableShippingPo
): number | undefined {
  const totalCube = getRawPoCube(po);
  return totalCube === undefined ? undefined : po.cubeCapacityFt3 - totalCube;
}

function getMoveUnitCube(
  po: EditableShippingPo,
  row: BulkOrderCreationRow
): number {
  const existingLine = po.lines.find((line) => line.sku === row.sku);
  return existingLine?.cubicFeetPerUnit ?? cubicFeetPerUnitFor(row);
}

export function buildProjectedPoAfterMove(
  po: EditableShippingPo,
  sourceRows: BulkOrderCreationRow[],
  quantitiesBySku: Record<string, number>
): EditableShippingPo {
  const projectedPo: EditableShippingPo = {
    ...po,
    lines: po.lines.map((line) => ({ ...line })),
  };

  for (const [sku, quantity] of Object.entries(quantitiesBySku)) {
    if (quantity <= 0) continue;
    const sourceRow = sourceRows.find((row) => row.sku === sku);
    if (!sourceRow) {
      throw new Error(`Unable to find source row for SKU ${sku}.`);
    }

    let line = projectedPo.lines.find((candidate) => candidate.sku === sku);
    if (!line) {
      addSkuLineToPo(projectedPo, sourceRow);
      line = projectedPo.lines.find((candidate) => candidate.sku === sku);
    }
    if (!line) {
      throw new Error(`Unable to project SKU ${sku} onto PO ${po.id}.`);
    }
    line.qty += quantity;
  }

  return projectedPo;
}

export function projectPoTotalsAfterMove(
  po: EditableShippingPo,
  sourceRows: BulkOrderCreationRow[],
  quantitiesBySku: Record<string, number>
): ShippingPoTotals {
  return computePoTotals(
    buildProjectedPoAfterMove(po, sourceRows, quantitiesBySku)
  );
}

interface ProportionalLeftoverMovePlan {
  quantitiesBySku: Record<string, number>;
  availableCapacityFt3: number | undefined;
  capacityMeasurable: boolean;
}

export function planProportionalLeftoverMove(
  po: EditableShippingPo,
  sourceRows: BulkOrderCreationRow[],
  requestedQuantitiesBySku: Record<string, number>
): ProportionalLeftoverMovePlan {
  const availableCapacityFt3 = getAvailableCapacityFt3(po);
  const quantitiesBySku = { ...requestedQuantitiesBySku };
  const positiveRequestedLines: {
    sku: string;
    quantity: number;
    order: number;
    unitCube: number;
  }[] = [];
  for (const [order, [sku, quantity]] of Object.entries(
    requestedQuantitiesBySku
  ).entries()) {
    if (!Number.isInteger(quantity) || quantity < 0) {
      throw new RangeError(
        `Move quantity for SKU ${sku} must be a whole number.`
      );
    }
    if (quantity === 0) continue;
    const sourceRow = sourceRows.find((row) => row.sku === sku);
    if (!sourceRow) {
      throw new Error(`Unable to find source row for SKU ${sku}.`);
    }
    positiveRequestedLines.push({
      sku,
      quantity,
      order,
      unitCube: getMoveUnitCube(po, sourceRow),
    });
  }

  const capacityMeasurable =
    availableCapacityFt3 !== undefined &&
    positiveRequestedLines.every(
      (line) => Number.isFinite(line.unitCube) && line.unitCube > 0
    );
  if (!capacityMeasurable) {
    return {
      quantitiesBySku,
      availableCapacityFt3,
      capacityMeasurable: false,
    };
  }

  const requestedCube = positiveRequestedLines.reduce(
    (total, line) => total + line.unitCube * line.quantity,
    0
  );
  const usableCapacity = Math.max(0, availableCapacityFt3);
  const ratio =
    requestedCube > 0 ? Math.min(1, usableCapacity / requestedCube) : 1;
  const remainders = positiveRequestedLines.map((line) => {
    const scaledQuantity = line.quantity * ratio;
    const wholeQuantity = Math.floor(scaledQuantity);
    quantitiesBySku[line.sku] = wholeQuantity;
    return {
      ...line,
      fractionalRemainder: scaledQuantity - wholeQuantity,
      wholeQuantity,
    };
  });
  let remainingCapacity =
    usableCapacity -
    remainders.reduce(
      (total, line) => total + line.unitCube * line.wholeQuantity,
      0
    );
  const roundingTolerance =
    Number.EPSILON *
    Math.max(1, po.cubeCapacityFt3, Math.abs(remainingCapacity)) *
    8;

  const orderedRemainders = remainders
    .filter((line) => line.wholeQuantity < line.quantity)
    .toSorted(
      (left, right) =>
        right.fractionalRemainder - left.fractionalRemainder ||
        left.order - right.order
    );
  for (const line of orderedRemainders) {
    if (line.unitCube > remainingCapacity + roundingTolerance) continue;
    quantitiesBySku[line.sku] = line.wholeQuantity + 1;
    remainingCapacity -= line.unitCube;
  }

  return {
    quantitiesBySku,
    availableCapacityFt3,
    capacityMeasurable: true,
  };
}

/**
 * Returns the largest whole quantity of `line` that fits alongside the PO's
 * other lines. An undefined result means cube cannot be evaluated safely
 * (for example, a zero/invalid per-unit cube or invalid capacity); in that
 * case quantity remains bounded by the source order, not by an invented cube
 * estimate.
 */
export function getMaxQuantityWithinCapacity(
  po: EditableShippingPo,
  line: EditableShippingPoLine
): number | undefined {
  if (
    !Number.isFinite(po.cubeCapacityFt3) ||
    po.cubeCapacityFt3 <= 0 ||
    !Number.isFinite(line.cubicFeetPerUnit) ||
    line.cubicFeetPerUnit <= 0
  ) {
    return undefined;
  }

  let otherLinesCube = 0;
  for (const otherLine of po.lines) {
    if (otherLine === line) continue;
    if (
      !Number.isFinite(otherLine.qty) ||
      otherLine.qty < 0 ||
      !Number.isFinite(otherLine.cubicFeetPerUnit) ||
      otherLine.cubicFeetPerUnit < 0
    ) {
      return undefined;
    }
    otherLinesCube += otherLine.cubicFeetPerUnit * otherLine.qty;
  }

  const remainingCube = po.cubeCapacityFt3 - otherLinesCube;
  const roundingTolerance =
    Number.EPSILON *
    Math.max(1, po.cubeCapacityFt3, Math.abs(otherLinesCube)) *
    8;
  return Math.max(
    0,
    Math.floor((remainingCube + roundingTolerance) / line.cubicFeetPerUnit)
  );
}

export function computeHeaderTotalCostUsd(
  pos: EditableShippingPo[]
): number | null {
  let total = 0;
  for (const po of pos) {
    const cost = computePoTotals(po).totalCostUsd;
    if (cost === null) return null;
    total += cost;
  }
  return total;
}

export function computeSourceQtyBySku(
  rows: BulkOrderCreationRow[]
): Map<string, number> {
  const map = new Map<string, number>();
  const divisionsBySku = new Map<string, Set<string>>();
  for (const row of rows) {
    const divisions = divisionsBySku.get(row.sku) ?? new Set<string>();
    divisions.add(row.division ?? "");
    divisionsBySku.set(row.sku, divisions);
  }
  for (const row of rows) {
    const divisions = divisionsBySku.get(row.sku);
    const key =
      divisions && divisions.size > 1
        ? allocationSkuKey(row.sku, row.division)
        : row.sku;
    map.set(key, (map.get(key) ?? 0) + row.requestedUnits);
  }
  return map;
}

function allocationSkuKey(sku: string, division?: string): string {
  return `${division ?? ""}\u0000${sku}`;
}

function sourceQtyKey(
  sku: string,
  division: string | undefined,
  sourceQtyBySku: Map<string, number>
): string {
  const divisionKey = allocationSkuKey(sku, division);
  return sourceQtyBySku.has(divisionKey) ? divisionKey : sku;
}

function sameDivision(
  left: EditableShippingPo,
  right: EditableShippingPo
): boolean {
  return (
    (left.division ?? "").trim().toUpperCase() ===
    (right.division ?? "").trim().toUpperCase()
  );
}

export function formatCurrencyUsd(amount: number | null): string {
  return amount === null ? "Unavailable" : `$${amount.toFixed(2)}`;
}

function parseCurrencyInput(raw: string): number | undefined {
  const cleaned = raw.replaceAll(/[^0-9.-]/g, "").trim();
  if (cleaned === "") return undefined;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Validates and (if valid) applies an edited price to `line`, returning the
 * error message to display (empty string clears any prior error). Does not
 * mutate `line` when the input is invalid, so the last committed value is
 * retained.
 */
export function applyPriceInput(
  line: EditableShippingPoLine,
  rawValue: string
): string {
  const parsed = parseCurrencyInput(rawValue);
  if (parsed === undefined || parsed < 0) {
    return "Enter a price of $0.00 or more.";
  }
  line.unitPriceUsd = Math.round(parsed * 100) / 100;
  return "";
}

function parseQtyInput(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) return undefined;
  return parsed;
}

/**
 * Validates and (if valid) applies an edited quantity to `line`, enforcing
 * that the aggregate quantity for this SKU across all suggested POs does not
 * exceed the quantity requested on the source order. Returns the error
 * message to display (empty string clears any prior error). Does not mutate
 * `line` when the input is invalid.
 */
export function applyQtyInput(
  allPos: EditableShippingPo[],
  line: EditableShippingPoLine,
  rawValue: string,
  sourceQtyBySku: Map<string, number>
): string {
  const parsed = parseQtyInput(rawValue);
  if (parsed === undefined || parsed < 0) {
    return "Enter a whole quantity of 0 or more.";
  }

  const sourcePo = allPos.find((po) => po.lines.includes(line));
  const otherLinesTotal = allPos
    .filter((po) => !sourcePo || sameDivision(po, sourcePo))
    .flatMap((po) => po.lines)
    .filter((candidate) => candidate !== line && candidate.sku === line.sku)
    .reduce((sum, candidate) => sum + candidate.qty, 0);
  const prospectiveTotal = otherLinesTotal + parsed;
  const sourceQty =
    sourceQtyBySku.get(
      sourceQtyKey(line.sku, sourcePo?.division, sourceQtyBySku)
    ) ?? 0;

  if (prospectiveTotal > sourceQty) {
    return `Allocating ${String(prospectiveTotal)} exceeds the ${String(sourceQty)} available for SKU ${line.sku}.`;
  }

  line.qty = parsed;
  return "";
}

/** Public wrapper over the internal whole-quantity parser (UX-37). */
export function parseWholeQuantityInput(raw: string): number | undefined {
  return parseQtyInput(raw);
}

/**
 * Derives, per SKU, how many units of the source order are not currently
 * allocated to any suggested PO line (UX-37 rule 1: a SKU's total quantity
 * is finite). This "leftover" amount is not stored separately; it is always
 * `sourceQty - (sum of that SKU's qty across every PO line)`, so it shrinks
 * automatically as lines are given more quantity and grows automatically as
 * lines are given less.
 */
export function computeLeftoverQtyBySku(
  allPos: EditableShippingPo[],
  sourceQtyBySku: Map<string, number>
): Map<string, number> {
  const allocated = new Map<string, number>();
  for (const po of allPos) {
    for (const line of po.lines) {
      const key = sourceQtyKey(line.sku, po.division, sourceQtyBySku);
      allocated.set(key, (allocated.get(key) ?? 0) + line.qty);
    }
  }
  const leftover = new Map<string, number>();
  for (const [sku, sourceQty] of sourceQtyBySku) {
    leftover.set(sku, sourceQty - (allocated.get(sku) ?? 0));
  }
  return leftover;
}

/**
 * Derives the current, live Leftover Inventory display rows (BUG-6). Rows
 * that never fit any suggested PO (generation's `leftoverRows`) and SKUs
 * whose allocated quantity has simply been reduced below their source total
 * are the same underlying concept — unallocated SKU quantity — so both are
 * covered by a single derivation from `computeLeftoverQtyBySku` rather than
 * a static, generation-time snapshot. SKUs with no remaining quantity are
 * omitted; the remaining quantity is this SKU's first source row with
 * `requestedUnits`/`cubicFeet` scaled to that quantity. Does not mutate its
 * inputs.
 */
export function computeLiveLeftoverRows(
  allPos: EditableShippingPo[],
  sourceRows: BulkOrderCreationRow[]
): BulkOrderCreationRow[] {
  const sourceQtyBySku = computeSourceQtyBySku(sourceRows);
  const leftoverQtyBySku = computeLeftoverQtyBySku(allPos, sourceQtyBySku);
  const firstRowBySku = new Map<string, BulkOrderCreationRow>();
  for (const row of sourceRows) {
    const key = sourceQtyKey(row.sku, row.division, sourceQtyBySku);
    if (!firstRowBySku.has(key)) firstRowBySku.set(key, row);
  }

  const leftoverRows: BulkOrderCreationRow[] = [];
  for (const [key, remainingQty] of leftoverQtyBySku) {
    if (remainingQty <= 0) continue;
    const sourceRow = firstRowBySku.get(key);
    if (!sourceRow) continue;
    leftoverRows.push({
      ...sourceRow,
      ...(key === sourceRow.sku ? {} : { inventoryKey: key }),
      requestedUnits: remainingQty,
      cubicFeet:
        Math.round(cubicFeetPerUnitFor(sourceRow) * remainingQty * 10) / 10,
    });
  }
  return leftoverRows;
}

interface OtherSkuLineRef {
  po: EditableShippingPo;
  line: EditableShippingPoLine;
}

/** Every line (across all suggested POs) for `sku` other than `excludeLine`. */
export function findOtherLinesForSku(
  allPos: EditableShippingPo[],
  sku: string,
  excludeLine: EditableShippingPoLine
): OtherSkuLineRef[] {
  const sourcePo = allPos.find((po) => po.lines.includes(excludeLine));
  const refs: OtherSkuLineRef[] = [];
  for (const po of allPos) {
    if (sourcePo && !sameDivision(po, sourcePo)) continue;
    for (const line of po.lines) {
      if (line === excludeLine || line.sku !== sku) continue;
      refs.push({ po, line });
    }
  }
  return refs;
}

/**
 * Distributes `total` whole units across `capacities` proportionally to each
 * entry's share of the combined capacity, using largest-remainder rounding
 * so the returned allocations sum to as much of `total` as fits (never more
 * than `total`, and never more than the paired capacity) and any amount that
 * cannot be placed is simply left unallocated by the caller (UX-37: this is
 * how an automatic-redistribution remainder ends up in leftover inventory).
 * Returns all zeros when `total` or the combined capacity is zero or less.
 */
export function allocateProportionally(
  capacities: number[],
  total: number
): number[] {
  const sumCapacity = capacities.reduce((sum, capacity) => sum + capacity, 0);
  if (total <= 0 || sumCapacity <= 0) return capacities.map(() => 0);

  const clampedTotal = Math.min(total, sumCapacity);
  const raw = capacities.map(
    (capacity) => (capacity / sumCapacity) * clampedTotal
  );
  const allocations = raw.map((value) => Math.floor(value));
  let remaining =
    clampedTotal - allocations.reduce((sum, value) => sum + value, 0);

  const byRemainingFractionDesc = raw
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .toSorted((a, b) => b.fraction - a.fraction);

  for (const { index } of byRemainingFractionDesc) {
    if (remaining <= 0) break;
    if (allocations[index] === undefined) continue;
    if (allocations[index] >= (capacities[index] ?? 0)) continue;
    allocations[index] += 1;
    remaining -= 1;
  }

  return allocations;
}

interface CapacityRedistributionCandidate {
  po: EditableShippingPo;
  line: EditableShippingPoLine | undefined;
  sourceRow: BulkOrderCreationRow;
  maxAdditionalQty: number;
  additionalQty: number;
}

interface CapacityRedistributionPlan {
  unallocatedQty: number;
  remainingQty: number;
  candidates: CapacityRedistributionCandidate[];
}

export interface CapacityRecipientPreview {
  poSequence: number;
  currentQty: number;
  hasSkuLine: boolean;
  maxAdditionalQty: number;
  additionalQty: number;
}

export type CapacityWarningChoice =
  | { type: "fit" }
  | { type: "override" }
  | { type: "remove" }
  | { type: "manual"; quantity: number }
  | { type: "redistribute" };

/**
 * Plans a proportional, cube-capped allocation of all source units left
 * unallocated after `targetQty` is applied. POs without measurable positive
 * per-unit cube are excluded rather than assigned an unbounded capacity.
 * This function does not mutate any PO.
 */
export function planCapacityAwareRedistribution(
  allPos: EditableShippingPo[],
  targetPo: EditableShippingPo,
  targetLine: EditableShippingPoLine,
  targetQty: number,
  sourceQtyBySku: Map<string, number>,
  sourceRow: BulkOrderCreationRow
): CapacityRedistributionPlan {
  const allocatedAfterTargetQty = allPos
    .filter((po) => sameDivision(po, targetPo))
    .flatMap((po) => po.lines)
    .filter((line) => line.sku === targetLine.sku)
    .reduce(
      (sum, line) => sum + (line === targetLine ? targetQty : line.qty),
      0
    );
  const unallocatedQty = Math.max(
    0,
    (sourceQtyBySku.get(
      sourceQtyKey(targetLine.sku, targetPo.division, sourceQtyBySku)
    ) ?? 0) - allocatedAfterTargetQty
  );
  const sourceCubePerUnit = cubicFeetPerUnitFor(sourceRow);

  const eligible = allPos.flatMap((po) => {
    if (
      po === targetPo ||
      !sameDivision(po, targetPo) ||
      !Number.isFinite(po.cubeCapacityFt3)
    )
      return [];
    const line = po.lines.find((candidate) => candidate.sku === targetLine.sku);
    const cubePerUnit = line?.cubicFeetPerUnit ?? sourceCubePerUnit;
    if (cubePerUnit <= 0 || !Number.isFinite(cubePerUnit)) return [];

    let usedCube = 0;
    for (const candidate of po.lines) {
      if (
        !Number.isFinite(candidate.qty) ||
        candidate.qty < 0 ||
        !Number.isFinite(candidate.cubicFeetPerUnit) ||
        candidate.cubicFeetPerUnit < 0
      ) {
        return [];
      }
      usedCube += candidate.cubicFeetPerUnit * candidate.qty;
    }

    const remainingCube = po.cubeCapacityFt3 - usedCube;
    const roundingTolerance =
      Number.EPSILON * Math.max(1, po.cubeCapacityFt3, Math.abs(usedCube)) * 8;
    const maxAdditionalQty = Math.max(
      0,
      Math.floor((remainingCube + roundingTolerance) / cubePerUnit)
    );
    return maxAdditionalQty > 0 ? [{ po, line, maxAdditionalQty }] : [];
  });

  const additionalQuantities = allocateProportionally(
    eligible.map((candidate) => candidate.maxAdditionalQty),
    unallocatedQty
  );
  const candidates = eligible.map((candidate, index) => ({
    ...candidate,
    sourceRow,
    additionalQty: additionalQuantities[index] ?? 0,
  }));
  const allocatedQty = candidates.reduce(
    (sum, candidate) => sum + candidate.additionalQty,
    0
  );

  return {
    unallocatedQty,
    remainingQty: unallocatedQty - allocatedQty,
    candidates,
  };
}

/** Applies a fresh, confirmed capacity-aware redistribution plan. */
export function applyCapacityAwareRedistribution(
  plan: CapacityRedistributionPlan,
  sku: string
): void {
  for (const candidate of plan.candidates) {
    if (candidate.additionalQty <= 0) continue;
    let line = candidate.line;
    if (!line) {
      addSkuLineToPo(candidate.po, candidate.sourceRow);
      line = candidate.po.lines.find((item) => item.sku === sku);
    }
    if (!line) {
      throw new Error(
        `Unable to add SKU ${sku} to PO ${String(candidate.po.sequence)}.`
      );
    }
    line.qty += candidate.additionalQty;
  }
}

export interface QuantityIncreaseReduction {
  po: EditableShippingPo;
  line: EditableShippingPoLine;
  reduceBy: number;
}

export interface QuantityIncreasePlan {
  line: EditableShippingPoLine;
  newQty: number;
  fromLeftover: number;
  reductions: QuantityIncreaseReduction[];
}

type QuantityIncreaseOutcome =
  | { kind: "apply-directly" }
  | { kind: "insufficient"; message: string }
  | { kind: "needs-confirmation"; plan: QuantityIncreasePlan };

/**
 * Determines how a quantity increase on `line` should be fulfilled (UX-37
 * rule 2). Increases covered entirely by this SKU's leftover inventory apply
 * without confirmation. Increases that also need units from other suggested
 * POs return a plan (proportional to each other line's current quantity,
 * largest-remainder rounded) for the caller to confirm with the user before
 * applying. Returns "insufficient" when the SKU does not have enough units
 * across leftover inventory and other POs combined.
 */
export function planQuantityIncrease(
  allPos: EditableShippingPo[],
  line: EditableShippingPoLine,
  newQty: number,
  sourceQtyBySku: Map<string, number>
): QuantityIncreaseOutcome {
  const delta = newQty - line.qty;
  if (delta <= 0) return { kind: "apply-directly" };

  const leftoverBySku = computeLeftoverQtyBySku(allPos, sourceQtyBySku);
  const sourcePo = allPos.find((po) => po.lines.includes(line));
  const key = sourceQtyKey(line.sku, sourcePo?.division, sourceQtyBySku);
  const availableLeftover = Math.max(0, leftoverBySku.get(key) ?? 0);
  if (delta <= availableLeftover) return { kind: "apply-directly" };

  const remainder = delta - availableLeftover;
  const others = findOtherLinesForSku(allPos, line.sku, line);
  const otherCapacity = others.reduce((sum, ref) => sum + ref.line.qty, 0);

  if (remainder > otherCapacity) {
    // Algebraically, leftover + every other line's quantity together always
    // equal (sourceQty - line.qty), so this branch is only reachable when
    // newQty itself exceeds the SKU's entire source order quantity.
    const sourceQty = sourceQtyBySku.get(key) ?? 0;
    return {
      kind: "insufficient",
      message: `Allocating ${String(newQty)} exceeds the ${String(sourceQty)} available for SKU ${line.sku}.`,
    };
  }

  const reduceAmounts = allocateProportionally(
    others.map((ref) => ref.line.qty),
    remainder
  );
  const reductions: QuantityIncreaseReduction[] = others
    .map((ref, index) => ({
      po: ref.po,
      line: ref.line,
      reduceBy: reduceAmounts[index] ?? 0,
    }))
    .filter((reduction) => reduction.reduceBy > 0);

  return {
    kind: "needs-confirmation",
    plan: { line, newQty, fromLeftover: availableLeftover, reductions },
  };
}

/** Applies a confirmed `QuantityIncreasePlan`, mutating every affected line. */
export function applyQuantityIncreasePlan(plan: QuantityIncreasePlan): void {
  for (const reduction of plan.reductions) {
    reduction.line.qty -= reduction.reduceBy;
  }
  plan.line.qty = plan.newQty;
}

/**
 * One SKU's released units awaiting disposition (UX-17). Carries the source
 * order row so a target PO that lacks the SKU can gain a correctly priced
 * line via `addSkuLineToPo`.
 */
export interface ReleasedLine {
  sku: string;
  qty: number;
  /** Per-unit cube used when a target PO does not already carry this SKU. */
  cubicFeetPerUnit: number;
  /** Authoritative pack size from the generated/source PO row, when available. */
  pieceCount?: number | null;
  /** Source order row providing metadata/prices for added SKU lines. */
  sourceRow: BulkOrderCreationRow;
}

export type DispositionCandidateLabel =
  | "matching-sku"
  | "matching-ship-date"
  | "has-capacity"
  | "no-fitting-pack";

/**
 * A same-division destination PO eligible to receive released units (UX-17).
 * A candidate may carry several labels at once. POs with a missing ship date
 * count as a ship-date match but are flagged so the UI can display
 * "No ship date" instead of implying equality.
 */
export interface DispositionCandidate {
  po: EditableShippingPo;
  labels: DispositionCandidateLabel[];
  shipDateMissing: boolean;
  /** Raw spare cube; undefined when the PO's cube cannot be measured. */
  spareCubeFt3: number | undefined;
  /** Released SKUs this PO already carries. */
  matchingSkus: string[];
  /** Released SKUs for which this PO can currently fit at least one full pack. */
  fittingSkus: string[];
  /** Reasons a released SKU cannot fit normally; shown without hiding the PO. */
  unavailableReasons: string[];
}

/**
 * Builds the combined candidate list for the UX-17 disposition dialog: every
 * same-division PO (except the source) that matches a released SKU, matches
 * the source PO's ship date (missing dates count as matching), or can fit a
 * full authoritative pack. Same-division POs remain visible even when no
 * complete pack fits, so spare cube is never presented as sufficient by
 * itself.
 */
export function buildDispositionCandidates(
  allPos: EditableShippingPo[],
  sourcePo: EditableShippingPo,
  releasedLines: ReleasedLine[]
): DispositionCandidate[] {
  const sourceShipTime = sourcePo.shipDateValue?.getTime();
  const candidates: DispositionCandidate[] = [];
  for (const po of allPos) {
    if (po.id === sourcePo.id || !sameDivision(po, sourcePo)) continue;
    const matchingSkus = [
      ...new Set(
        releasedLines
          .filter((released) =>
            po.lines.some((line) => line.sku === released.sku)
          )
          .map((released) => released.sku)
      ),
    ];
    const spareCubeFt3 = getAvailableCapacityFt3(po);
    const candidateShipTime = po.shipDateValue?.getTime();
    const shipDateMissing = candidateShipTime === undefined;
    const labels: DispositionCandidateLabel[] = [];
    const fittingSkus: string[] = [];
    const unavailableReasons: string[] = [];
    for (const released of releasedLines) {
      const pieceCount = getReleasedPieceCount(released);
      const targetLine = po.lines.find((line) => line.sku === released.sku);
      const targetPieceCount = targetLine?.pieceCount;
      if (pieceCount === undefined) {
        unavailableReasons.push(`${released.sku}: PieceCount unavailable.`);
        continue;
      }
      if (released.qty < pieceCount) {
        unavailableReasons.push(
          `${released.sku}: fewer than ${String(pieceCount)} units are available for a complete pack.`
        );
        continue;
      }
      if (
        isValidPieceCount(targetPieceCount) &&
        targetPieceCount !== pieceCount
      ) {
        unavailableReasons.push(
          `${released.sku}: recipient PieceCount does not match the source.`
        );
        continue;
      }
      if (spareCubeFt3 === undefined) {
        unavailableReasons.push(`${released.sku}: capacity unavailable.`);
        continue;
      }
      const cubePerUnit =
        targetLine?.cubicFeetPerUnit ?? released.cubicFeetPerUnit;
      if (!Number.isFinite(cubePerUnit) || cubePerUnit <= 0) {
        unavailableReasons.push(`${released.sku}: unit cube unavailable.`);
        continue;
      }
      const packCube = cubePerUnit * pieceCount;
      const roundingTolerance =
        Number.EPSILON *
        Math.max(1, po.cubeCapacityFt3, Math.abs(spareCubeFt3)) *
        8;
      if (spareCubeFt3 + roundingTolerance >= packCube) {
        fittingSkus.push(released.sku);
      } else {
        unavailableReasons.push(
          `${released.sku}: spare cube cannot fit a ${String(pieceCount)}-unit pack.`
        );
      }
    }
    if (matchingSkus.length > 0) labels.push("matching-sku");
    if (
      shipDateMissing ||
      (sourceShipTime !== undefined && candidateShipTime === sourceShipTime)
    )
      labels.push("matching-ship-date");
    labels.push(fittingSkus.length > 0 ? "has-capacity" : "no-fitting-pack");
    candidates.push({
      po,
      labels,
      shipDateMissing,
      spareCubeFt3,
      matchingSkus,
      fittingSkus,
      unavailableReasons,
    });
  }
  return candidates;
}

/** True only for a positive integer pack size obtained from an authoritative row. */
function isValidPieceCount(
  pieceCount: number | null | undefined
): pieceCount is number {
  return Number.isInteger(pieceCount) && (pieceCount ?? 0) > 0;
}

function getReleasedPieceCount(released: ReleasedLine): number | undefined {
  const pieceCount =
    released.pieceCount === undefined
      ? released.sourceRow.pieceCount
      : released.pieceCount;
  return isValidPieceCount(pieceCount) ? pieceCount : undefined;
}

/** The user's per-target choice in the UX-17 disposition dialog. */
export interface BatchDispositionSelection {
  poId: string;
  /** Explicit per-target consent to receive units beyond measurable spare cube. */
  allowOverCapacity: boolean;
}

/** The dialog's confirmed outcome, replayed through `planBatchDisposition`. */
export interface BatchDispositionResult {
  selections: BatchDispositionSelection[];
  /**
   * true sends any capacity shortfall to leftover inventory; false places it
   * in targets that were explicitly acknowledged for over-capacity.
   */
  remainderToLeftover: boolean;
  /** Optional route for complete packs that do not fit existing POs. */
  newPoMode?: "only" | "remainder";
  newPoMetadata?: {
    deliverTo: string;
    shipDateValue: Date | undefined;
    arrivalDate: Date | undefined;
  };
}

interface SkuDispositionAllocation {
  po: EditableShippingPo;
  qty: number;
  /** Units placed beyond the target's measurable spare cube (acknowledged). */
  overCapacityQty: number;
  sourceRow: BulkOrderCreationRow;
}

export interface SkuDispositionPlan {
  sku: string;
  releasedQty: number;
  /** Missing when authoritative pack metadata could not be obtained. */
  pieceCount?: number;
  /** Units that can never be distributed as a complete pack. */
  incompletePackQty: number;
  allocations: SkuDispositionAllocation[];
  /** Units left for (derived) leftover inventory. */
  leftoverQty: number;
}

export interface BatchDispositionTargetSummary {
  po: EditableShippingPo;
  plannedQty: number;
  initialSpareCubeFt3: number | undefined;
  /** True when any planned unit exceeds this target's measurable spare cube. */
  exceedsCapacity: boolean;
}

export interface BatchDispositionPlan {
  perSku: SkuDispositionPlan[];
  perTarget: BatchDispositionTargetSummary[];
  totalLeftoverQty: number;
  /**
   * Units that cannot be placed within the selected targets' spare cube,
   * before the remainder's explicit second choice (leftover vs acknowledged
   * over-capacity targets) is applied.
   */
  capacityShortfallQty: number;
}

interface BatchDispositionOptions {
  /** A local, uncommitted draft PO used only when a new-PO route is selected. */
  newPo?: EditableShippingPo;
  newPoMode?: "only" | "remainder";
}

function sameDivisionValue(left?: string, right?: string): boolean {
  return (
    (left ?? "").trim().toUpperCase() === (right ?? "").trim().toUpperCase()
  );
}

function compatiblePieceCount(
  candidate: DispositionCandidate,
  released: ReleasedLine,
  pieceCount: number
): boolean {
  const targetPieceCount = candidate.po.lines.find(
    (line) => line.sku === released.sku
  )?.pieceCount;
  return (
    !isValidPieceCount(targetPieceCount) || targetPieceCount === pieceCount
  );
}

/**
 * Plans every released line in whole packs. Each selected PO's spare cube is
 * shared across the batch; incomplete packs and unknown pack sizes remain in
 * leftover inventory. A user-selected new PO is capacity-limited, while an
 * existing PO can exceed capacity only after explicit UX-17 consent.
 */
export function planBatchDisposition(
  candidates: DispositionCandidate[],
  selections: BatchDispositionSelection[],
  releasedLines: ReleasedLine[],
  remainderToOverCapacity: boolean,
  options: BatchDispositionOptions = {}
): BatchDispositionPlan {
  const selectionByPoId = new Map(
    selections.map((selection) => [selection.poId, selection])
  );
  const selected = candidates.filter((candidate) =>
    selectionByPoId.has(candidate.po.id)
  );
  const spareByPoId = new Map<string, number>();
  for (const candidate of selected) {
    if (candidate.spareCubeFt3 !== undefined)
      spareByPoId.set(candidate.po.id, candidate.spareCubeFt3);
  }
  const plannedQtyByPoId = new Map<string, number>();
  const overQtyByPoId = new Map<string, number>();
  const perSku: SkuDispositionPlan[] = [];
  let capacityShortfallQty = 0;
  let totalLeftoverQty = 0;
  let remainingNewPoCube = options.newPo
    ? getAvailableCapacityFt3(options.newPo)
    : undefined;

  const cubePerUnitFor = (
    candidate: DispositionCandidate,
    released: ReleasedLine
  ): number =>
    candidate.po.lines.find((line) => line.sku === released.sku)
      ?.cubicFeetPerUnit ?? released.cubicFeetPerUnit;

  for (const released of releasedLines) {
    if (!Number.isInteger(released.qty) || released.qty < 0) {
      throw new RangeError(
        `Released quantity for SKU ${released.sku} must be a whole number.`
      );
    }
    const pieceCount = getReleasedPieceCount(released);
    const allocations: SkuDispositionAllocation[] = [];
    const sourceRow: BulkOrderCreationRow = {
      ...released.sourceRow,
      unitCube: released.cubicFeetPerUnit,
      cubicFeet: released.sourceRow.requestedUnits * released.cubicFeetPerUnit,
      ...(pieceCount === undefined ? {} : { pieceCount }),
    };
    const targetsForSku =
      options.newPoMode === "only"
        ? []
        : selected.filter((candidate) =>
            sameDivisionValue(
              candidate.po.division,
              released.sourceRow.division
            )
          );
    const eligible =
      pieceCount === undefined
        ? []
        : targetsForSku.flatMap((candidate) => {
            const spare = spareByPoId.get(candidate.po.id);
            if (
              spare === undefined ||
              !compatiblePieceCount(candidate, released, pieceCount)
            )
              return [];
            const cubePerUnit = cubePerUnitFor(candidate, released);
            if (!Number.isFinite(cubePerUnit) || cubePerUnit <= 0) return [];
            const roundingTolerance =
              Number.EPSILON *
              Math.max(1, candidate.po.cubeCapacityFt3, Math.abs(spare)) *
              8;
            const packCube = cubePerUnit * pieceCount;
            const maxPacks = Math.max(
              0,
              Math.floor((spare + roundingTolerance) / packCube)
            );
            return [{ candidate, cubePerUnit, maxPacks }];
          });
    const releasedPackCount =
      pieceCount === undefined ? 0 : Math.floor(released.qty / pieceCount);
    const fittedPacks = allocateProportionally(
      eligible.map((entry) => entry.maxPacks),
      releasedPackCount
    );
    let placedQty = 0;
    for (const [index, entry] of eligible.entries()) {
      const qty = (fittedPacks[index] ?? 0) * (pieceCount ?? 0);
      if (qty <= 0 || pieceCount === undefined) continue;
      placedQty += qty;
      const poId = entry.candidate.po.id;
      spareByPoId.set(
        poId,
        (spareByPoId.get(poId) ?? 0) - qty * entry.cubePerUnit
      );
      plannedQtyByPoId.set(poId, (plannedQtyByPoId.get(poId) ?? 0) + qty);
      allocations.push({
        po: entry.candidate.po,
        qty,
        overCapacityQty: 0,
        sourceRow,
      });
    }

    let remainderQty = released.qty - placedQty;
    capacityShortfallQty += remainderQty;

    if (
      remainderQty > 0 &&
      options.newPoMode &&
      options.newPo &&
      pieceCount !== undefined &&
      sameDivisionValue(options.newPo.division, released.sourceRow.division)
    ) {
      const cubePerUnit =
        options.newPo.lines.find((line) => line.sku === released.sku)
          ?.cubicFeetPerUnit ?? released.cubicFeetPerUnit;
      if (
        Number.isFinite(cubePerUnit) &&
        cubePerUnit > 0 &&
        remainingNewPoCube !== undefined
      ) {
        const packCube = cubePerUnit * pieceCount;
        const roundingTolerance =
          Number.EPSILON *
          Math.max(
            1,
            options.newPo.cubeCapacityFt3,
            Math.abs(remainingNewPoCube)
          ) *
          8;
        const maxPacks = Math.max(
          0,
          Math.floor((remainingNewPoCube + roundingTolerance) / packCube)
        );
        const qty =
          Math.min(Math.floor(remainderQty / pieceCount), maxPacks) *
          pieceCount;
        if (qty > 0) {
          remainingNewPoCube -= qty * cubePerUnit;
          plannedQtyByPoId.set(
            options.newPo.id,
            (plannedQtyByPoId.get(options.newPo.id) ?? 0) + qty
          );
          allocations.push({
            po: options.newPo,
            qty,
            overCapacityQty: 0,
            sourceRow,
          });
          placedQty += qty;
          remainderQty -= qty;
        }
      }
    }

    if (
      remainderQty > 0 &&
      remainderToOverCapacity &&
      !options.newPoMode &&
      pieceCount !== undefined
    ) {
      const remainderPacks = Math.floor(remainderQty / pieceCount);
      const acknowledged = selected.filter(
        (candidate) =>
          sameDivisionValue(
            candidate.po.division,
            released.sourceRow.division
          ) &&
          compatiblePieceCount(candidate, released, pieceCount) &&
          selectionByPoId.get(candidate.po.id)?.allowOverCapacity
      );
      if (acknowledged.length > 0) {
        const overPacks = allocateProportionally(
          acknowledged.map(() => remainderPacks),
          remainderPacks
        );
        for (const [index, candidate] of acknowledged.entries()) {
          const qty = (overPacks[index] ?? 0) * pieceCount;
          if (qty <= 0) continue;
          const poId = candidate.po.id;
          const cubePerUnit = cubePerUnitFor(candidate, released);
          if (spareByPoId.has(poId) && Number.isFinite(cubePerUnit)) {
            spareByPoId.set(
              poId,
              (spareByPoId.get(poId) ?? 0) - qty * cubePerUnit
            );
          }
          plannedQtyByPoId.set(poId, (plannedQtyByPoId.get(poId) ?? 0) + qty);
          overQtyByPoId.set(poId, (overQtyByPoId.get(poId) ?? 0) + qty);
          const existing = allocations.find(
            (allocation) => allocation.po.id === poId
          );
          if (existing) {
            existing.qty += qty;
            existing.overCapacityQty += qty;
          } else {
            allocations.push({
              po: candidate.po,
              qty,
              overCapacityQty: qty,
              sourceRow,
            });
          }
          placedQty += qty;
          remainderQty -= qty;
        }
      }
    }

    totalLeftoverQty += remainderQty;
    perSku.push({
      sku: released.sku,
      releasedQty: released.qty,
      ...(pieceCount === undefined ? {} : { pieceCount }),
      incompletePackQty:
        pieceCount === undefined ? 0 : released.qty % pieceCount,
      allocations,
      leftoverQty: remainderQty,
    });
  }

  const perTarget: BatchDispositionTargetSummary[] = selected.map(
    (candidate) => {
      const plannedQty = plannedQtyByPoId.get(candidate.po.id) ?? 0;
      return {
        po: candidate.po,
        plannedQty,
        initialSpareCubeFt3: candidate.spareCubeFt3,
        exceedsCapacity:
          (overQtyByPoId.get(candidate.po.id) ?? 0) > 0 ||
          (candidate.spareCubeFt3 === undefined && plannedQty > 0),
      };
    }
  );
  if (options.newPo && (plannedQtyByPoId.get(options.newPo.id) ?? 0) > 0) {
    perTarget.push({
      po: options.newPo,
      plannedQty: plannedQtyByPoId.get(options.newPo.id) ?? 0,
      initialSpareCubeFt3: getAvailableCapacityFt3(options.newPo),
      exceedsCapacity: false,
    });
  }

  return { perSku, perTarget, totalLeftoverQty, capacityShortfallQty };
}

/**
 * Applies a confirmed `BatchDispositionPlan`, adding SKU lines (with the
 * source row's metadata and prices) to targets that lack them. Leftover
 * amounts need no mutation because leftover inventory is derived.
 */
export function applyBatchDispositionPlan(plan: BatchDispositionPlan): void {
  const normalCubeByPo = new Map<EditableShippingPo, number>();
  for (const skuPlan of plan.perSku) {
    const pieceCount = skuPlan.pieceCount;
    for (const allocation of skuPlan.allocations) {
      if (
        !isValidPieceCount(pieceCount) ||
        allocation.qty <= 0 ||
        !Number.isInteger(allocation.qty) ||
        allocation.qty % pieceCount !== 0 ||
        !sameDivisionValue(
          allocation.po.division,
          allocation.sourceRow.division
        )
      ) {
        throw new Error(
          `Invalid pack or division allocation for SKU ${skuPlan.sku}.`
        );
      }
      if (allocation.qty > allocation.overCapacityQty) {
        const line = allocation.po.lines.find(
          (candidate) => candidate.sku === skuPlan.sku
        );
        const cubePerUnit =
          line?.cubicFeetPerUnit ?? cubicFeetPerUnitFor(allocation.sourceRow);
        if (!Number.isFinite(cubePerUnit) || cubePerUnit <= 0) {
          throw new Error(
            `Capacity is unavailable for SKU ${skuPlan.sku}; placement was not applied.`
          );
        }
        normalCubeByPo.set(
          allocation.po,
          (normalCubeByPo.get(allocation.po) ?? 0) +
            (allocation.qty - allocation.overCapacityQty) * cubePerUnit
        );
      }
    }
  }
  for (const [po, additionalCube] of normalCubeByPo) {
    const spareCube = getAvailableCapacityFt3(po);
    const tolerance =
      Number.EPSILON *
      Math.max(1, po.cubeCapacityFt3, Math.abs(additionalCube)) *
      8;
    if (spareCube === undefined || additionalCube > spareCube + tolerance) {
      throw new Error(
        `Capacity changed for PO ${String(po.sequence)}; placement was not applied.`
      );
    }
  }
  for (const skuPlan of plan.perSku) {
    for (const allocation of skuPlan.allocations) {
      if (allocation.qty <= 0) continue;
      let line = allocation.po.lines.find(
        (candidate) => candidate.sku === skuPlan.sku
      );
      if (!line) {
        addSkuLineToPo(allocation.po, allocation.sourceRow);
        line = allocation.po.lines.find(
          (candidate) => candidate.sku === skuPlan.sku
        );
      }
      if (!line) {
        throw new Error(
          `Unable to add SKU ${skuPlan.sku} to PO ${String(allocation.po.sequence)}.`
        );
      }
      line.qty += allocation.qty;
    }
  }
}
